import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'

import {
  Icon,
  TextStyle,
  Button,
  PageLoader,
  Popup,
  Heading,
  Align,
  Margin,
  Spacer
} from 'component-library-next'

import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { getClientPath } from '@app/modules/ClientsProfile'
import { CourtCaseDialog } from '@app/modules/Messages/Components/ProfileBox/CourtCase/CourtCaseDialog'
import { RecommendUpgradeButton } from '@app/modules/Messages/Components/RecommendUpgrade/RecommendUpgradeButton'
import TextArea from '@app/modules/Messages/Components/TextArea/TextArea'
import { letterRequestContext } from '@app/modules/Messages/letterRequestContext'
import { getSalutation } from '@app/modules/Messages/utils/gender'
import { useCancelLetterRequestMutation } from '@app/queries/CancelLetterRequest/CancelLetterRequest.queries'
import { CourtCaseQuery } from '@app/queries/CourtCases/CourtCase.queries'
import { useCreateOrUpdateCustomerNoteMutation } from '@app/queries/CreateOrUpdateCustomerNote/CreateOrUpdateCustomerNote.queries'
import { refetchCustomerAsLawyerQuery } from '@app/queries/CustomerAsLawyer/CustomerAsLawyer.queries'
import { Customer } from '@app/queries/CustomerAsLawyer/models'
import { refetchPostponedLetterRequestsQuery } from '@app/queries/PostponedLetterRequests/PostponedLetterRequests.queries'

import openLetterRequest from './openLetterRequest.svg'

import './styles.scss'

type Props = {
  customer: Customer | CourtCaseQuery['courtCases']['items'][number]['customer']
  latestPurchase?: string
  upgradesTo?: number[] | null
}

const ProfileBox: React.FunctionComponent<Props> = ({ customer, latestPurchase, upgradesTo }) => {
  const { letterRequestData, letterLoading, refetchLetter } = useContext(letterRequestContext)
  const [cancelRequest, { loading: cancelRequestLoading }] = useCancelLetterRequestMutation({
    refetchQueries: [
      refetchPostponedLetterRequestsQuery(),
      refetchCustomerAsLawyerQuery({ customerId: customer.id })
    ]
  })
  const [showCancelRequestPopup, setShowCancelRequestPopup] = useState(false)
  const noteInProfileBox = useSelector(isFeatureEnabled(Features.CUSTOMER_NOTE_IN_PROFILE_BOX))
  const [createOrUpdateCustomerNote] = useCreateOrUpdateCustomerNoteMutation()
  const [note, setNote] = useState('')
  const [timer, setTimer] = useState()
  const { courtCaseUuid } = useParams<{ courtCaseUuid: string }>()

  useEffect(() => {
    if (!!noteInProfileBox && customer.note?.note) {
      setNote(customer.note.note)
    }
  }, [])

  if (!customer || !latestPurchase) {
    return null
  }

  const debouncedSaveNote = async (newNote: string) => {
    setNote(newNote)
    clearTimeout(timer)
    const debouncedTimer = setTimeout(async () => {
      await createOrUpdateCustomerNote({
        variables: {
          customerId: customer.id,
          note: newNote
        }
      })
    }, 2000)
    setTimer(debouncedTimer as any)
  }

  let iconName
  // remove 'Upgrade' string from product name if present
  const productName = latestPurchase.replace('Upgrade ', '')

  switch (productName) {
    case 'Basis':
      iconName = Icon.names.shieldBasis
      break
    case 'Pro':
      iconName = Icon.names.shieldPro
      break
    case 'Premium':
      iconName = Icon.names.shieldPremium
  }

  const memberSince = customer.membership?.activatedAt
    ? new Date(customer.membership.activatedAt).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      })
    : null
  const latestUpgradeDate = customer.membership?.advancedFrom
    ? new Date(customer.membership.startsAt).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      })
    : null

  const handleCancelRequest = async () => {
    await cancelRequest({
      variables: {
        letterRequestId: letterRequestData?.openLetterRequest?.letterRequestId as any
      }
    })
    typeof refetchLetter === 'function' && (await refetchLetter())
    setShowCancelRequestPopup(false)
    return
  }

  const showLoader = cancelRequestLoading || letterLoading
  const hasMobileNumber = customer.validGermanMobileNumber || customer.mobilePhone

  return (
    <div className='profile-box__wrapper'>
      {showLoader && <PageLoader loading />}
      <span className='profile-box__customer-name'>
        {getSalutation(customer.gender)}
        <br />
        {customer.firstName + ' ' + customer.lastName}
      </span>

      <div className='profile-box__product-wrapper'>
        {iconName && <Icon alt={'product type'} name={iconName} />}
        <span
          className={`profile-box__product-label profile-box__product-${productName.toLowerCase()}`}
        >
          {productName}
        </span>
      </div>

      <div className='profile-box__mitglied-wrapper'>
        <span className='profile-box__mitglied-label'>Mitgliedsnummer:</span>
        <span className='profile-box__mitglied-value'>{customer.membership?.membershipNumber}</span>
        {memberSince && (
          <>
            <span className='profile-box__mitglied-label'>Mitglied seit:</span>
            <span className='profile-box__mitglied-value'>{memberSince}</span>
          </>
        )}
        {latestUpgradeDate && (
          <>
            <span className='profile-box__mitglied-label'>Upgrade:</span>
            <span className='profile-box__mitglied-value'>{latestUpgradeDate}</span>
          </>
        )}
        {letterRequestData?.letterAllowance?.displayValue && (
          <>
            <span className='profile-box__mitglied-label'>Anwaltsschreiben:</span>
            <span className='profile-box__mitglied-value'>
              {letterRequestData?.letterAllowance?.displayValue}
            </span>
          </>
        )}
      </div>
      <div className='profile-box__contact-wrapper'>
        <Icon alt={'phone'} name={Icon.names.phoneBlack} />
        {hasMobileNumber && (
          <>
            <span>{customer.validGermanMobileNumber || customer.mobilePhone}</span>
            <span>{/* placeholder to keep the form of the grid*/}</span>
          </>
        )}
        {customer.phone && <span>{customer.phone}</span>}
        <Icon alt={'file'} name={Icon.names.fileBlack} />
        <Link className='profile-box__link' to={getClientPath(customer.id)}>
          Alle Dokumente
        </Link>
      </div>
      {letterRequestData?.openLetterRequest && (
        <div className='profile-box__letter-request-wrapper'>
          <TextStyle size={TextStyle.sizes.small}>
            Anwaltsschreiben angefordert:{' '}
            {new Date(letterRequestData?.openLetterRequest?.createdAt).toLocaleDateString('de-DE')}{' '}
          </TextStyle>
          <div className='letter-request-icon'>
            <img alt='letter' src={openLetterRequest} />
          </div>
          <Button
            size={Button.sizes.tiny}
            color={Button.colors.white}
            onClick={() => setShowCancelRequestPopup(true)}
          >
            ANFRAGE ABLEHNEN
          </Button>
        </div>
      )}
      {showCancelRequestPopup && (
        <Popup onCloseButtonClick={() => setShowCancelRequestPopup(false)}>
          <Align
            flexDirection={Align.flexDirections.column}
            alignItem={Align.alignItemDirections.center}
          >
            <Heading size={Heading.sizes.medium} color={Heading.colors.black}>
              Anfrage für Anwaltsschreiben ablehnen?
            </Heading>
            <TextStyle size={TextStyle.sizes.small}>
              Möchten Sie die Anfrage für ein Anwaltsschreiben wirklich ablehnen?
            </TextStyle>
          </Align>
          <Spacer />
          <Align alignItem={Align.alignItemDirections.center} direction={Align.directions.center}>
            <Margin>
              <Button onClick={handleCancelRequest} color={Button.colors.warning}>
                Schreiben ablehnen
              </Button>
            </Margin>
            <Margin>
              <Button onClick={() => setShowCancelRequestPopup(false)} color={Button.colors.minor}>
                Nein, zurück
              </Button>
            </Margin>
          </Align>
        </Popup>
      )}
      <RecommendUpgradeButton customerId={customer.id} upgradesTo={upgradesTo} />
      {!!noteInProfileBox && !courtCaseUuid && (
        <TextArea
          className='profile-box__textarea'
          value={note}
          placeholder='Klicken Sie hier um Text einzugeben.'
          input={{
            onChange: (e) => debouncedSaveNote(e.target.value)
          }}
        />
      )}
      <CourtCaseDialog customer={customer} />
    </div>
  )
}

export default ProfileBox
